import React, { FC, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  TextField,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import { isValidPhoneNumber } from "react-phone-number-input";
import { toastOptions } from "config/constants";
import { useAppDispatch, useAppSelector } from "config/hooks";
import accountsAPI, { InvitationDetails, InvitationRole } from "api/accounts";
import { completeSignIn } from "reducers/auth";
import PhoneNumberInput from "components/PhoneNumberInput";
import PasswordInput from "Pages/Account/Settings/PasswordInput";
import pendingInvitation from "utils/pendingInvitation";

const ROLE_LABELS: Record<InvitationRole, string> = {
  payee: "Payee",
  manage: "Manager",
  production: "Production",
  owner: "Owner",
};

const MIN_PASSWORD_LENGTH = 8;
// Same limits owl checks on register (devise password_length tops out at 128).
const MAX_NAME_LENGTH = 100;
const MAX_PASSWORD_LENGTH = 128;

export type RegistrationField =
  | "first_name"
  | "last_name"
  | "phone"
  | "password"
  | "password_confirmation";

export type RegistrationErrors = Partial<Record<RegistrationField, string>>;

export interface RegistrationValues {
  first_name: string;
  last_name: string;
  phone?: string;
  password: string;
  password_confirmation: string;
}

// Form order, used to focus the first field with an error.
const FIELDS: { field: RegistrationField; id: string; label: string }[] = [
  { field: "first_name", id: "first-name", label: "First name" },
  { field: "last_name", id: "last-name", label: "Last name" },
  { field: "phone", id: "phone", label: "Phone" },
  { field: "password", id: "password", label: "Password" },
  {
    field: "password_confirmation",
    id: "password-confirmation",
    label: "Password confirmation",
  },
];

// Names are expected trimmed; passwords are checked as typed.
export const validateRegistration = (
  values: RegistrationValues,
): RegistrationErrors => {
  const errors: RegistrationErrors = {};
  if (!values.first_name) errors.first_name = "Enter your first name";
  else if (values.first_name.length > MAX_NAME_LENGTH)
    errors.first_name = `First name must be ${MAX_NAME_LENGTH} characters or fewer`;
  if (!values.last_name) errors.last_name = "Enter your last name";
  else if (values.last_name.length > MAX_NAME_LENGTH)
    errors.last_name = `Last name must be ${MAX_NAME_LENGTH} characters or fewer`;
  if (!values.phone) errors.phone = "Enter your phone number";
  else if (!isValidPhoneNumber(values.phone))
    errors.phone = "Enter a valid phone number";
  if (!values.password) errors.password = "Enter a password";
  else if (values.password.length < MIN_PASSWORD_LENGTH)
    errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
  else if (values.password.length > MAX_PASSWORD_LENGTH)
    errors.password = `Password must be ${MAX_PASSWORD_LENGTH} characters or fewer`;
  if (!values.password_confirmation)
    errors.password_confirmation = "Confirm your password";
  else if (values.password_confirmation !== values.password)
    errors.password_confirmation = "Passwords do not match";
  return errors;
};

// owl answers a rejected registration with `errors` keyed by field
// ({ phone: ["can't be blank"] }). Returns those per field, plus a message
// for anything that isn't a form field (expired invitation, existing account).
export const registrationServerErrors = (
  err: any,
): { fields: RegistrationErrors; message?: string } => {
  const data = err?.response?.data;
  const fields: RegistrationErrors = {};
  const other: string[] = [data?.message, data?.error].filter(Boolean);
  if (Array.isArray(data?.errors)) {
    other.push(...data.errors.map(String));
  } else if (data?.errors) {
    Object.entries(data.errors).forEach(([key, value]) => {
      const text = [value].flat().join(", ");
      const known = FIELDS.find((f) => f.field === key);
      if (known) fields[known.field] = `${known.label} ${text}`;
      else
        other.push(key === "base" ? text : `${key.replace(/_/g, " ")} ${text}`);
    });
  }
  if (other.length === 0 && Object.keys(fields).length === 0) {
    other.push(err?.message || "Something went wrong");
  }
  return { fields, message: other.join(", ") || undefined };
};

const extractError = (err: any): string =>
  registrationServerErrors(err).message || "Something went wrong";

export const invitationSummary = (invitation: InvitationDetails): string => {
  const account = invitation.account_name || "A Riivet account";
  if (invitation.role === "payee")
    return `${account} has invited you to receive payments.`;
  const role = invitation.role ? ROLE_LABELS[invitation.role] : "a member";
  return `${account} has invited you to join as ${role}.`;
};

const Invitation: FC = () => {
  const { key = "" } = useParams();
  const dispatch = useAppDispatch();
  const isSignedIn = useAppSelector((state) => state.auth.isSignedIn);
  const currentUser = useAppSelector((state) => state.auth.currentUser);

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState<string | undefined>();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<RegistrationErrors>({});
  // Errors show once "Create account" is clicked, then follow each edit.
  const [submitted, setSubmitted] = useState(false);

  const values: RegistrationValues = {
    first_name: firstName.trim(),
    last_name: lastName.trim(),
    phone,
    password,
    password_confirmation: confirm,
  };

  // Re-checks the edited field (and the confirmation when the password
  // changes), replacing any server error on it.
  const revalidate = (changes: Partial<RegistrationValues>) => {
    if (!submitted) return;
    const next = validateRegistration({ ...values, ...changes });
    const touched = Object.keys(changes) as RegistrationField[];
    if (touched.includes("password")) touched.push("password_confirmation");
    setErrors((prev) => {
      const merged = { ...prev };
      touched.forEach((f) => {
        if (next[f]) merged[f] = next[f];
        else delete merged[f];
      });
      return merged;
    });
  };

  const showErrors = (fieldErrors: RegistrationErrors) => {
    setErrors(fieldErrors);
    const first = FIELDS.find(({ field }) => fieldErrors[field]);
    if (first) document.getElementById(first.id)?.focus();
  };

  const load = useCallback(async () => {
    try {
      setInvitation(await accountsAPI.getInvitation(key));
    } catch (err: any) {
      setInvitation({ status: err?.response?.data?.status || "missing" });
    }
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  const register = async () => {
    setSubmitted(true);
    setFirstName(values.first_name);
    setLastName(values.last_name);
    const clientErrors = validateRegistration(values);
    showErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    setIsLoading(true);
    try {
      const res = await accountsAPI.registerFromInvitation(key, {
        ...values,
        phone: values.phone!.trim(),
      });
      toast.success("Welcome to Riivet", toastOptions);
      await completeSignIn(res)(dispatch);
    } catch (err) {
      const { fields, message } = registrationServerErrors(err);
      showErrors(fields);
      if (message) toast.error(message, toastOptions);
      setIsLoading(false);
    }
  };

  // For the inputs that aren't MUI TextFields (no built-in helperText).
  const fieldError = (field: RegistrationField) =>
    errors[field] ? (
      <FormHelperText error>{errors[field]}</FormHelperText>
    ) : null;

  const accept = async () => {
    setIsLoading(true);
    try {
      const res = await accountsAPI.acceptInvitation(key);
      toast.success("Invitation accepted", toastOptions);
      // Reload the account list so the new account/role shows up. Payee
      // invitations add an account of the user's own, so only a role
      // invitation names the account to switch to.
      const accountId = res.role === "payee" ? undefined : res.account_id;
      await completeSignIn(currentUser, accountId)(dispatch);
    } catch (err) {
      toast.error(extractError(err), toastOptions);
      setIsLoading(false);
    }
  };

  const status = invitation?.status;

  const body = () => {
    if (!invitation)
      return <p className="text-center text-muted">Loading invitation...</p>;
    switch (status) {
      case "register":
        return (
          <>
            <p className="text-center">{invitationSummary(invitation)}</p>
            <p className="text-center text-muted">
              Create your account to accept.
            </p>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  id="invitation-email"
                  label="Email"
                  value={invitation.email || ""}
                  fullWidth
                  size="small"
                  slotProps={{ htmlInput: { readOnly: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  id="first-name"
                  label="First name"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    revalidate({ first_name: e.target.value.trim() });
                  }}
                  error={!!errors.first_name}
                  helperText={errors.first_name}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  id="last-name"
                  label="Last name"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    revalidate({ last_name: e.target.value.trim() });
                  }}
                  error={!!errors.last_name}
                  helperText={errors.last_name}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid size={12}>
                <InputLabel
                  htmlFor="phone"
                  error={!!errors.phone}
                  sx={{ mb: 0.5 }}
                >
                  Phone
                </InputLabel>
                <PhoneNumberInput
                  id="phone"
                  value={phone}
                  invalid={!!errors.phone}
                  onChange={(value) => {
                    setPhone(value);
                    revalidate({ phone: value });
                  }}
                />
                {fieldError("phone")}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel
                  htmlFor="password"
                  error={!!errors.password}
                  sx={{ mb: 0.5 }}
                >
                  Password
                </InputLabel>
                <PasswordInput
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  invalid={!!errors.password}
                  onChange={(value) => {
                    setPassword(value);
                    revalidate({ password: value });
                  }}
                />
                {fieldError("password")}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel
                  htmlFor="password-confirmation"
                  error={!!errors.password_confirmation}
                  sx={{ mb: 0.5 }}
                >
                  Confirm password
                </InputLabel>
                <PasswordInput
                  id="password-confirmation"
                  autoComplete="new-password"
                  value={confirm}
                  invalid={!!errors.password_confirmation}
                  onChange={(value) => {
                    setConfirm(value);
                    revalidate({ password_confirmation: value });
                  }}
                />
                {fieldError("password_confirmation")}
              </Grid>
            </Grid>
          </>
        );
      case "login":
        return (
          <>
            <p className="text-center">{invitationSummary(invitation)}</p>
            <p className="text-center text-muted">
              {isSignedIn
                ? `This invitation was sent to ${invitation.email}. Sign out and sign in as that user to accept it.`
                : `You already have a Riivet account (${invitation.email}). Sign in to accept.`}
            </p>
          </>
        );
      case "accept":
        return <p className="text-center">{invitationSummary(invitation)}</p>;
      case "accepted":
        return (
          <p className="text-center">
            This invitation has already been accepted.
          </p>
        );
      case "expired":
        return (
          <p className="text-center">
            This invitation has expired. Ask the account owner to send a new
            one.
          </p>
        );
      default:
        return (
          <p className="text-center text-danger">
            This invitation link is not valid.
          </p>
        );
    }
  };

  const action = () => {
    if (status === "register") {
      return (
        <Button
          variant="contained"
          size="large"
          disabled={isLoading}
          onClick={register}
        >
          Create account
        </Button>
      );
    }
    if (status === "accept") {
      return (
        <Button
          variant="contained"
          size="large"
          disabled={isLoading}
          onClick={accept}
        >
          Accept invitation
        </Button>
      );
    }
    if (status === "login" && !isSignedIn) {
      return (
        <Button
          component={Link}
          to="/signin"
          variant="contained"
          size="large"
          onClick={() => pendingInvitation.set(key)}
        >
          Sign in to accept
        </Button>
      );
    }
    return null;
  };

  return (
    <div className="h-100 bg-plum-plate bg-animation">
      <div className="d-flex h-100 justify-content-center align-items-center">
        <Box
          className="mx-auto app-login-box"
          sx={{ width: { xs: "100%", md: "66.66%" } }}
        >
          <div className="modal-dialog w-100 mx-auto">
            <div className="modal-content">
              <div className="modal-body">
                <div className="app-logo mx-auto mb-3" />
                <h4 className="text-center mb-3">You're invited</h4>
                {body()}
              </div>
              <div className="modal-footer clearfix">
                <div className="float-left">
                  {isSignedIn ? (
                    <Link to="/">Back to Riivet</Link>
                  ) : (
                    <Link to="/signin">Back to sign in</Link>
                  )}
                </div>
                <div className="float-right">{action()}</div>
              </div>
            </div>
          </div>
        </Box>
      </div>
      <ToastContainer autoClose={2000} />
    </div>
  );
};

export default Invitation;
